import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { adminAPI, type StatisticsData as APIStatisticsData } from '@/api/admin';
import { handleApiError } from '@/lib/api';

interface ESMetrics {
  total_queries: number;
  avg_query_time: number;
  slow_queries_count: number;
  recent_avg_time: number;
  indices_count: number;
  slow_indices: Array<{
    name: string;
    avg_time: number;
    count: number;
    hits: number;
  }>;
  last_update: string;
}

interface ChartData {
  hourly: Array<{
    hour: string;
    avg_time: number;
    count: number;
    max_time: number;
  }>;
  indices: Array<{
    name: string;
    avg_time: number;
    count: number;
    max_time: number;
    hits: number;
  }>;
}

interface StatisticsData {
  total_queries: number;
  avg_query_time: number;
  slow_queries_count: number;
  recent_avg_time: number;
  indices_count: number;
  slow_indices: Array<{
    name: string;
    avg_time: number;
    count: number;
    hits: number;
  }>;
  last_update: string;
}

export function Statistics() {
  const [stats, setStats] = useState<APIStatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchStatistics();
    // Обновляем статистику каждые 30 секунд
    const interval = setInterval(fetchStatistics, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getESPerformance();
      setStats(data);
    } catch (error) {
      console.error('Error fetching statistics:', handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleResetMetrics = async () => {
    if (!confirm('Вы уверены, что хотите сбросить все метрики?')) return;

    try {
      setResetting(true);
      const data = await adminAPI.resetESMetrics();
      if (data.ok) {
        fetchStatistics();
      }
    } catch (error) {
      console.error('Error resetting metrics:', handleApiError(error));
    } finally {
      setResetting(false);
    }
  };

  if (loading && !stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Статистика и аналитика</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Загрузка статистики...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Статистика производительности</h2>
            <p className="text-muted-foreground">Метрики производительности Elasticsearch и системы</p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/admin">← Назад к обзору</Link>
          </Button>
        </div>
      {/* Общая статистика */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Всего запросов</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_queries || 0}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Среднее время</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.avg_query_time ? `${stats.avg_query_time.toFixed(0)}ms` : '0ms'}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Медленных запросов</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.slow_queries_count || 0}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Индексов</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.indices_count || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Производительность индексов */}
      <Card>
        <CardHeader>
          <CardTitle>Производительность индексов</CardTitle>
          <CardDescription>
            Статистика по времени выполнения запросов в разных индексах
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Детальная статистика по индексам временно недоступна</p>
          </div>
        </CardContent>
      </Card>

      {/* Медленные индексы */}
      {stats?.slow_indices && stats.slow_indices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Медленные индексы</CardTitle>
            <CardDescription>
              Индексы с производительностью ниже нормы
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.slow_indices.map((index: { name: string; avg_time: number; count: number; hits: number }) => (
                <div key={index.name} className="flex items-center justify-between p-3 border rounded bg-yellow-50 dark:bg-yellow-900/20">
                  <div>
                    <span className="font-medium text-yellow-800 dark:text-yellow-200">
                      {index.name}
                    </span>
                    <div className="text-sm text-muted-foreground">
                      Запросов: {index.count} • Результатов: {index.hits}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-yellow-800 dark:text-yellow-200">
                      {(index.avg_time * 1000).toFixed(0)}ms
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Последние запросы */}
      <Card>
        <CardHeader>
          <CardTitle>Последние запросы</CardTitle>
          <CardDescription>
            Недавние поисковые запросы в системе
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">История недавних запросов временно недоступна</p>
          </div>
        </CardContent>
      </Card>

      {/* Управление */}
      <Card>
        <CardHeader>
          <CardTitle>Управление метриками</CardTitle>
          <CardDescription>
            Сброс и обновление статистики
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4">
            <Button onClick={fetchStatistics} disabled={loading}>
              {loading ? 'Обновление...' : 'Обновить'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleResetMetrics}
              disabled={resetting}
            >
              {resetting ? 'Сброс...' : 'Сбросить метрики'}
            </Button>
          </div>
          {stats?.last_update && (
            <p className="text-sm text-muted-foreground mt-4">
              Последнее обновление: {new Date(stats.last_update).toLocaleString()}
            </p>
          )}
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
